// Text annotations of the "Medidas" menu: free text ("Texto"), spine labels ("Marcas Columna") and the
// arrow's text ("Flecha"). All text entry goes through in-page dialogs, never window.prompt.
import {
  type AnnotationToolClass,
  baseAnnotationTool,
  builtInTool,
  csCore,
  csTools,
  type Handle,
  type InteractionType,
  type MeasurementData,
  type MouseDetail,
  type Point,
  type RenderDetail,
} from './cstypes';
import { canvasContext, drawTextBox, pointInBox } from './draw';
import { chooseSpineLabel, promptText } from './dialogs';

export const TEXT_PROMPT = 'Escriba la anotación:';
const NEAR_TEXT_PX = 10;
const SPINE_BACKGROUND = '#ffeb00';
const SPINE_TEXT_COLOR = '#000';

/** C1–C7, T1–T12, L1–L5, S1–S5, in the order spine labelling walks them. */
export const SPINE_LABELS: readonly string[] = [
  ...Array.from({ length: 7 }, (_, i) => `C${i + 1}`),
  ...Array.from({ length: 12 }, (_, i) => `T${i + 1}`),
  ...Array.from({ length: 5 }, (_, i) => `L${i + 1}`),
  ...Array.from({ length: 5 }, (_, i) => `S${i + 1}`),
];
export const DEFAULT_SPINE_LABEL = 'L3';

function textReadout(data: MeasurementData): void {
  data.readout = data.text ? [{ label: 'Texto:', value: data.text }] : [];
}

function nearTextBox(element: HTMLElement, handle: Handle, coords: Point): boolean {
  if (pointInBox(handle.boundingBox, coords)) return true;
  const at = csCore().pixelToCanvas(element, handle);
  return Math.hypot(at.x - coords.x, at.y - coords.y) < NEAR_TEXT_PX;
}

// --- Texto ---------------------------------------------------------------------------------------

/** Free text placed with a click; double-click edits it (an empty text deletes it). */
export function createTextTool(): AnnotationToolClass {
  const Base = baseAnnotationTool();
  return class TextAnnotationTool extends Base {
    constructor(name = 'TextAnnotation') {
      super({ name, supportedInteractionTypes: ['mouse', 'touch'] });
    }

    createNewMeasurement(detail: MouseDetail): MeasurementData {
      const at = detail.currentPoints.image;
      return {
        visible: true,
        active: false,
        complete: true,
        handles: { end: { x: at.x, y: at.y, highlight: true, active: false, hasBoundingBox: true } },
      };
    }

    addNewMeasurement(evt: CustomEvent<MouseDetail>, _interactionType: InteractionType): void {
      evt.preventDefault();
      evt.stopPropagation();
      const detail = evt.detail;
      const data = this.createNewMeasurement(detail);
      void promptText(TEXT_PROMPT).then((text) => {
        if (!text) return;
        data.text = text;
        csTools().addToolState(detail.element, this.name, data);
        csCore().updateImage(detail.element);
      });
    }

    doubleClickCallback(evt: CustomEvent<MouseDetail>): boolean {
      const { element, currentPoints } = evt.detail;
      const toolData = csTools().getToolState(element, this.name)?.data as MeasurementData[] | undefined;
      const data = toolData?.find((d) => this.pointNearTool(element, d, currentPoints.canvas));
      if (!data) return false;
      evt.stopImmediatePropagation();
      evt.preventDefault();
      void promptText(TEXT_PROMPT, data.text ?? '').then((text) => {
        if (text === null) return;
        if (text === '') csTools().removeToolState(element, this.name, data);
        else data.text = text;
        csCore().updateImage(element);
      });
      return true;
    }

    pointNearTool(element: HTMLElement, data: MeasurementData, coords: Point): boolean {
      return data.visible !== false && data.readOnly !== true && nearTextBox(element, data.handles.end, coords);
    }

    distanceFromPoint(element: HTMLElement, data: MeasurementData, coords: Point): number {
      const at = csCore().pixelToCanvas(element, data.handles.end);
      return Math.hypot(at.x - coords.x, at.y - coords.y);
    }

    renderToolData(evt: CustomEvent<RenderDetail>): void {
      const { element } = evt.detail;
      const toolData = csTools().getToolState(element, this.name)?.data as MeasurementData[] | undefined;
      if (!toolData) return;
      const ctx = canvasContext(evt.detail.canvasContext);
      for (const data of toolData) {
        textReadout(data);
        if (data.visible === false || !data.text) continue;
        const color = csTools().toolColors.getColorIfActive(data);
        const at = csCore().pixelToCanvas(element, data.handles.end);
        data.handles.end.boundingBox = drawTextBox(ctx, [data.text], at.x, at.y, color, { centerX: true, centerY: true });
      }
    }
  };
}

// --- Marcas Columna -----------------------------------------------------------------------------

/**
 * Shared by every viewport's instance so the sequence (L3, L4, L5, S1…) continues wherever the
 * user clicks next. `current` is advanced by the beta's createNewMeasurement; no loop at S5.
 */
const spineConfiguration: Record<string, unknown> = {
  markers: SPINE_LABELS,
  current: '',
  ascending: true,
  loop: false,
  changeTextCallback: (data: MeasurementData, _detail: MouseDetail, done: (data: MeasurementData, text: string) => void) => {
    void promptText(TEXT_PROMPT, data.text ?? '').then((text) => {
      if (text) done(data, text);
      else done(data, data.text ?? '');
    });
  },
};

/** The beta TextMarkerTool, drawn as IMEXHS does: black label on a yellow box. */
export function createSpineLabelTool(): AnnotationToolClass {
  const Parent = builtInTool('TextMarkerTool');
  return class SpineLabelTool extends Parent {
    constructor(name = 'TextMarker') {
      super(name);
      this.configuration = spineConfiguration;
    }

    pointNearTool(element: HTMLElement, data: MeasurementData, coords: Point): boolean {
      return data.readOnly !== true && super.pointNearTool(element, data, coords);
    }

    renderToolData(evt: CustomEvent<RenderDetail>): void {
      const { element } = evt.detail;
      const toolData = csTools().getToolState(element, this.name)?.data as MeasurementData[] | undefined;
      if (!toolData) return;
      const ctx = canvasContext(evt.detail.canvasContext);
      for (const data of toolData) {
        textReadout(data);
        if (data.visible === false || !data.text) continue;
        const color = csTools().toolColors.getColorIfActive(data);
        const at = csCore().pixelToCanvas(element, data.handles.end);
        data.handles.end.boundingBox = drawTextBox(ctx, [data.text], at.x, at.y - 10, color, {
          background: SPINE_BACKGROUND,
          textColor: SPINE_TEXT_COLOR,
          centerX: true,
          centerY: true,
        });
      }
    }
  };
}

/**
 * "Marcación de Columna" modal: resolves true when the user picked a start label (labelling then
 * continues from it), false when cancelled.
 */
export async function startSpineLabelling(): Promise<boolean> {
  const initial = typeof spineConfiguration.current === 'string' && spineConfiguration.current ? spineConfiguration.current : DEFAULT_SPINE_LABEL;
  const choice = await chooseSpineLabel(SPINE_LABELS, SPINE_LABELS.includes(initial) ? initial : DEFAULT_SPINE_LABEL);
  if (!choice) return false;
  spineConfiguration.current = choice;
  return true;
}

// --- Flecha ---------------------------------------------------------------------------------------

/** The beta ArrowAnnotateTool; loaded read-only arrows are drawn but never picked. */
export function createArrowTool(): AnnotationToolClass {
  const Parent = builtInTool('ArrowAnnotateTool');
  return class ArrowTool extends Parent {
    constructor(name = 'ArrowAnnotate') {
      super(name);
    }

    pointNearTool(element: HTMLElement, data: MeasurementData, coords: Point): boolean {
      return data.readOnly !== true && super.pointNearTool(element, data, coords);
    }
  };
}

/** Configuration of the beta ArrowAnnotateTool: its text is asked for in-page instead of with prompt(). */
export const ARROW_CONFIGURATION: Record<string, unknown> = {
  getTextCallback: (done: (text: string | null) => void) => {
    void promptText(TEXT_PROMPT).then((text) => done(text || null));
  },
  changeTextCallback: (data: MeasurementData, _detail: MouseDetail, done: (text: string, deleteTool?: boolean) => void) => {
    void promptText(TEXT_PROMPT, data.text ?? '').then((text) => {
      if (text === null) done(data.text ?? '');
      else done(text, text === '');
    });
  },
  drawHandles: false,
  drawHandlesOnHover: true,
  arrowFirst: true,
};
